import type {
  ImageUnitData,
  InlineVideoUnitData,
  IframeUnitData,
  NormalizedContentUnit,
  RawAnswer,
  RawComponent,
  RawContentSection,
  RawContentUnit,
  RawDateMessage,
  RawQuestion,
  RawQuestionSettings,
  RawQuestionStats,
  RawQuestionSection,
  RawQuestionSetStats,
  RawSection,
  RawSectionStats,
  RawVariable,
  TextUnitData,
} from '@/features/content/useComponent';
import { resolveImageUnitSrc } from '@/features/content/imageUnitSrc';
import { normalizeContentUnit } from '@/features/content/useComponent';
import { resolveAttempts } from './attemptSettings';
import {
  mergeAiQuestionSettings,
  resolveAiHintThreshold,
  resolveAllowAiAssistant,
  resolveAllowAiHints,
  resolveAllowAiPracticeProblems,
} from './questionSettingsMerge';
import { readHintSetting, readSolutionsSetting } from './questionTimingSettings';
import type { ComponentTimingState } from '@/features/content/stats/timingUtils';

import {
  resolveRandomVariables,
  resolveRepresentativeRandomVariables,
  substituteVariablesInContent,
} from './randomVariables';

export interface NormalizedDisclosureData {
  isVisible: boolean;
  summaryLabel: string;
  units: NormalizedContentUnit[];
}

export interface NormalizedAnswerFeedbackData {
  description?: string;
  earnedPoints?: number;
  possiblePoints: number;
  status: 'correct' | 'incorrect' | 'partial' | 'participation' | 'saved';
  title: string;
}

export interface NormalizedRubricUnit {
  details: string;
  id: string;
  name: string;
  score: number;
}

export interface NormalizedGradingRubric {
  rubricUnits: NormalizedRubricUnit[];
  totalScore: number;
}

export interface NormalizedGradingDetails {
  feedback?: string;
  manualFeedback?: string;
  uploadFileUrl?: string;
}

export interface NormalizedMultipleChoiceOption {
  contentUnits?: NormalizedContentUnit[];
  id: string;
  index: number;
  label: string;
  text?: string;
}

export interface BaseQuestionData {
  // Attempts required before an AI hint is offered; 0 = immediately. Independent
  // of hintSetting (the authored-hint threshold).
  aiHintThreshold?: number;
  /**
   * The authored answer formula for a randomized question, e.g. "d * 0.445".
   *
   * INSTRUCTOR ONLY, and populated only for an instructor request rather than
   * populated always and hidden at render: the formula is the answer, and a
   * student who has their own numbers can finish the arithmetic. Keeping it out
   * of their normalized question means no component can leak it by rendering a
   * field it was handed.
   */
  answerFormula?: string;
  allowAIAssistant?: boolean;
  allowAIHints?: boolean;
  // Independent of allowAIHints. Falls back to it only when the component and
  // course both leave the practice setting unset (pre-split authoring).
  allowAIPracticeProblems?: boolean;
  attemptsLabel: string;
  attemptsUsed: number;
  canCheck: boolean;
  canSave: boolean;
  feedback?: NormalizedAnswerFeedbackData;
  hasAggregateStats?: boolean;
  // Whether the viewer has any answer at all, on any part. After the close a
  // question without one scores 0, as the section total counts it.
  hasResponse?: boolean;
  hintSetting?: number;
  hint?: NormalizedDisclosureData;
  id: string;
  isAssigned: boolean;
  // Single-attempt mode, for the four objective types: the attempts SETTING is
  // 1, so there is Save only and the answer is graded at the close. A limit
  // that the option count lowered to 1 is not it: that question offers one
  // check (issue #92). Unset for uploads and multi-part parents, whose attempt
  // rule reads their limit as it always has.
  isSingleAttemptMode?: boolean;
  // Removed from the assignment (SetQuestionRemoved): never rendered, and not
  // assigned, but kept in the list at its position, because the student save
  // sends question stats by position.
  isRemoved?: boolean;
  isInstructorView?: boolean;
  isInstructorStatsMode?: boolean;
  isLocked: boolean;
  isParticipationOnly: boolean;
  isPastDue?: boolean;
  isReviewMode?: boolean;
  imagePaths?: string[];
  kind:
    | 'aiUpload'
    | 'exactAnswer'
    | 'multiPart'
    | 'multipleChoice'
    | 'numerical'
    | 'selectAll';
  label: string;
  maxAttempts?: number;
  partLabel?: string;
  parentQuestionId?: string;
  pointsPossible: number;
  promptUnits: NormalizedContentUnit[];
  questionSectionId: string;
  scoreEarned?: number;
  sectionId: string;
  solution?: NormalizedDisclosureData;
  solutionsAvailableAtUtcMilli?: number;
  solutionsVisible?: boolean;
  uid: string;
  usesRandomVariables?: boolean;
  // True when the prompt shows an instructor-facing REPRESENTATIVE set of random
  // variable values (deterministic preview) rather than a specific student's
  // values. Drives the "each student sees their own numbers" note.
  usesRepresentativeVariableValues?: boolean;
}

export interface MultipleChoiceQuestionData extends BaseQuestionData {
  answerIndexStats?: number[];
  correctAnswerIndex?: number;
  currentRandomExactAnswer?: string;
  currentRandomNumericalAnswer?: number;
  currentRandomNumericalAnswerExtraExponent?: number;
  kind: 'multipleChoice';
  options: NormalizedMultipleChoiceOption[];
  selectedIndex?: number;
  units?: string;
}

/**
 * A select-all-that-apply question.
 *
 * Deliberately its own kind rather than multiple choice with a flag: every
 * existing `kind === 'multipleChoice'` check would otherwise silently include
 * select-all questions, which is the class of bug that made this type render as
 * a single-select radio group in the first place.
 *
 * `correctAnswerIndices` is plural for the same reason — the scalar
 * `correctAnswerIndex` multiple choice uses cannot express more than one correct
 * option, and reading it by accident would look like it worked.
 */
export interface SelectAllQuestionData extends BaseQuestionData {
  answerIndexStats?: number[];
  correctAnswerIndices: number[];
  kind: 'selectAll';
  options: NormalizedMultipleChoiceOption[];
  selectedIndices: number[];
  units?: string;
}

export interface NumericalQuestionData extends BaseQuestionData {
  correctAnswer?: number;
  correctAnswerExtraExponent?: number;
  currentRandomNumericalAnswer?: number;
  currentRandomNumericalAnswerExtraExponent?: number;
  // The resolved random-variable values (name + current_value) that PRODUCE
  // currentRandomNumericalAnswer, persisted with the student's answer so the
  // displayed variables and the graded answer stay a matched pair. Undefined
  // when there is nothing safe to persist (e.g. a credited legacy answer whose
  // original variables are unknown).
  currentRandomVariables?: RawVariable[];
  errorTolerance?: number;
  inputValue: string;
  kind: 'numerical';
  units?: string;
}

export interface ExactAnswerQuestionData extends BaseQuestionData {
  correctAnswer?: string;
  currentRandomExactAnswer?: string;
  inputValue: string;
  kind: 'exactAnswer';
  units?: string;
}

export interface MultiPartQuestionData extends BaseQuestionData {
  kind: 'multiPart';
  parts: NormalizedQuestionData[];
}

export interface AIUploadQuestionData extends BaseQuestionData {
  gradingDetails?: NormalizedGradingDetails;
  gradingRubric: NormalizedGradingRubric;
  kind: 'aiUpload';
  submittedFileName?: string;
  submittedFileUrl?: string;
}

export type NormalizedQuestionData =
  | AIUploadQuestionData
  | ExactAnswerQuestionData
  | MultiPartQuestionData
  | MultipleChoiceQuestionData
  | NumericalQuestionData
  | SelectAllQuestionData;

export interface NormalizedQuestionSectionData {
  // When the section closes for this viewer: their own due date, a time
  // extension included. A page left open checks it before sending an answer.
  closesAtUtcMilli?: number;
  earnedPoints?: number;
  hasAggregateStats?: boolean;
  heading: string;
  id: string;
  isInstructorView?: boolean;
  isPastDue?: boolean;
  kind: 'homework' | 'preLecture';
  possiblePoints: number;
  questionCount: number;
  questionSectionId: string;
  questions: NormalizedQuestionData[];
  rawQuestionStats: RawQuestionStats[];
  sectionNumAttempts: number;
  sectionStatsId: string;
  sectionTotalScore: number;
  solutionsVisible?: boolean;
}

type RawQuestionType =
  | 'AI_UPLOAD'
  | 'EXACT_ANSWER'
  | 'MULTI_PART'
  | 'MULTIPLE_CHOICE'
  | 'NUMERICAL_WITH_ERROR'
  | 'SELECT_ALL';

type RawQuestionAssignmentType =
  | 'ALL'
  | 'FIXED'
  | 'RANDOM'
  | 'USER_DEFINED';

type QuestionSectionKind = 'homework' | 'preLecture';

interface VariableReplacement {
  name: string;
  textValue: string;
}

const QUESTION_TYPE_MAP: Record<string, RawQuestionType> = {
  '0': 'MULTIPLE_CHOICE',
  '1': 'MULTIPLE_CHOICE',
  // 2 is SELECT_ALL. It was absent here, so a select-all question fell to the
  // MULTIPLE_CHOICE default and rendered as a radio group the student could not
  // answer it with.
  '2': 'SELECT_ALL',
  '3': 'NUMERICAL_WITH_ERROR',
  '4': 'EXACT_ANSWER',
  '5': 'MULTI_PART',
  '6': 'AI_UPLOAD',
  AI_UPLOAD: 'AI_UPLOAD',
  EXACT_ANSWER: 'EXACT_ANSWER',
  MULTI_PART: 'MULTI_PART',
  MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
  NUMERICAL_WITH_ERROR: 'NUMERICAL_WITH_ERROR',
  SELECT_ALL: 'SELECT_ALL',
  UNKNOWN: 'MULTIPLE_CHOICE',
};

const QUESTION_ASSIGNMENT_TYPE_MAP: Record<string, RawQuestionAssignmentType> = {
  '0': 'ALL',
  '1': 'ALL',
  '2': 'FIXED',
  '3': 'RANDOM',
  '4': 'USER_DEFINED',
  ALL: 'ALL',
  FIXED: 'FIXED',
  RANDOM: 'RANDOM',
  UNKNOWN: 'ALL',
  USER_DEFINED: 'USER_DEFINED',
};

const CONTENT_UNIT_TYPE_MAP: Record<string, 'IFRAME' | 'IMAGE' | 'TEXT' | 'VIDEO'> = {
  '1': 'TEXT',
  '2': 'IMAGE',
  '3': 'VIDEO',
  '4': 'IFRAME',
  IFRAME: 'IFRAME',
  IMAGE: 'IMAGE',
  TEXT: 'TEXT',
  VIDEO: 'VIDEO',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeIdentifier(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  return normalizeString(value);
}

function normalizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function normalizeNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toContentUnitArray(value: unknown): RawContentUnit[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawContentUnit[];
}

function toQuestionArray(value: unknown): RawQuestion[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestion[];
}

function toQuestionStatsArray(value: unknown): RawQuestionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawQuestionStats[];
}

function toSectionStatsArray(value: unknown): RawSectionStats[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isRecord) as RawSectionStats[];
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeString(entry))
    .filter((entry): entry is string => Boolean(entry));
}

function toIntegerArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeNumber(entry))
    .filter((entry): entry is number => entry !== undefined)
    .map((entry) => Math.trunc(entry))
    .filter((entry) => entry >= 0);
}

function toNumberArray(value: unknown): number[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => normalizeNumber(entry))
    .filter((entry): entry is number => entry !== undefined);
}

function getContentSection(value: unknown): RawContentSection | null {
  return isRecord(value) ? (value as RawContentSection) : null;
}

function getDateMessage(value: unknown): RawDateMessage | null {
  return isRecord(value) ? (value as RawDateMessage) : null;
}

function getTimestampMilliseconds(value: unknown): number | null {
  const timestamp = normalizeString(value);

  if (!timestamp) {
    return null;
  }

  const parsed = Number(timestamp);
  return Number.isFinite(parsed) ? parsed : null;
}

function getDateTimestamp(date?: RawDateMessage | null): number | null {
  if (!date?.year || !date?.month) {
    return null;
  }

  const safeDay = date.day && date.day > 0 ? date.day : 1;
  return Date.UTC(date.year, date.month - 1, safeDay);
}

function getDueTimestamp(component: RawComponent): number | null {
  return (
    getTimestampMilliseconds(component.end_time_utc_milli ?? component.endTimeUtcMilli) ??
    getDateTimestamp(getDateMessage(component.end_date ?? component.endDate))
  );
}

function getSectionIdentifier(section: RawSection, index: number): string {
  return (
    normalizeIdentifier(section.id) ??
    normalizeIdentifier(section.uid) ??
    `section-${index + 1}`
  );
}

function getQuestionSection(
  section: RawSection,
  kind: QuestionSectionKind,
): RawQuestionSection | null {
  const rawQuestionSection =
    kind === 'preLecture'
      ? section.pre_lecture_question_section ?? section.preLectureQuestionSection
      : section.question_section ?? section.questionSection;
  return isRecord(rawQuestionSection) ? (rawQuestionSection as RawQuestionSection) : null;
}

function getQuestionSectionIdentifier(
  section: RawSection,
  questionSection: RawQuestionSection,
  index: number,
): string {
  return (
    normalizeIdentifier(questionSection.id) ??
    normalizeIdentifier(questionSection.uid) ??
    getSectionIdentifier(section, index)
  );
}

/**
 * A multi-part question's part, as students see it named: "Part a", "Part b".
 * Edit mode names the parts it draws with the same rule.
 */
export function getPartLabel(partIndex: number): string {
  return `Part ${String.fromCharCode(97 + partIndex)}`;
}

function getQuestionSectionHeading(
  kind: QuestionSectionKind,
  sectionIndex: number,
  totalSections: number,
): string {
  if (kind === 'preLecture') {
    return totalSections > 1
      ? `Pre-Lecture Set ${sectionIndex + 1}`
      : 'Pre-Lecture Questions';
  }

  return totalSections > 1 ? `Homework Set ${sectionIndex + 1}` : 'Homework Questions';
}

function normalizeQuestionType(value: unknown): RawQuestionType | null {
  if (typeof value === 'number') {
    return QUESTION_TYPE_MAP[String(value)] ?? null;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return 'MULTIPLE_CHOICE';
  }

  return QUESTION_TYPE_MAP[normalized.toUpperCase()] ?? null;
}

function normalizeQuestionAssignmentType(
  value: unknown,
): RawQuestionAssignmentType {
  if (typeof value === 'number') {
    return QUESTION_ASSIGNMENT_TYPE_MAP[String(value)] ?? 'ALL';
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return 'ALL';
  }

  return QUESTION_ASSIGNMENT_TYPE_MAP[normalized.toUpperCase()] ?? 'ALL';
}

function normalizeContentUnitType(value: unknown) {
  if (typeof value === 'number') {
    return CONTENT_UNIT_TYPE_MAP[String(value)] ?? null;
  }

  const normalized = normalizeString(value);

  if (!normalized) {
    return null;
  }

  return CONTENT_UNIT_TYPE_MAP[normalized.toUpperCase()] ?? null;
}

function getHeadingText(unit: RawContentUnit, replacements: VariableReplacement[]) {
  const heading = normalizeString(unit.heading);
  return heading ? replaceVariablesInText(heading, replacements) : undefined;
}

function getCaptionText(unit: RawContentUnit, replacements: VariableReplacement[]) {
  const caption = normalizeString(unit.caption);
  return caption ? replaceVariablesInText(caption, replacements) : undefined;
}

function getSummaryLabel(
  heading: string | undefined,
  numberedHeading: string | undefined,
  unitPosition: number,
): string {
  return numberedHeading ?? heading ?? `Content item ${unitPosition}`;
}

function getImageAlt(
  heading: string | undefined,
  caption: string | undefined,
): string {
  return heading ?? caption ?? 'Question illustration';
}

function getImageSize(unit: RawContentUnit): ImageUnitData['size'] {
  const sizeValue = unit.image_size ?? unit.imageSize;

  if (typeof sizeValue === 'number') {
    if (sizeValue === 1) {
      return 'small';
    }

    if (sizeValue === 3) {
      return 'large';
    }

    return 'medium';
  }

  const normalized = normalizeString(sizeValue)?.toUpperCase();

  if (normalized === 'SMALL') {
    return 'small';
  }

  if (normalized === 'LARGE') {
    return 'large';
  }

  return 'medium';
}

function getWidthPercent(value: unknown): number {
  const parsed = normalizeNumber(value);

  if (parsed === undefined) {
    return 100;
  }

  return Math.min(100, Math.max(10, parsed));
}

function getHeightPixels(value: unknown): number {
  const parsed = normalizeNumber(value);

  if (parsed === undefined || parsed <= 0) {
    return 420;
  }

  return parsed;
}

function buildVariableReplacements(variables: RawVariable[] | null | undefined): VariableReplacement[] {
  if (!Array.isArray(variables)) {
    return [];
  }

  const replacements = variables
    .map((variable) => {
      const name = normalizeString(variable.name);

      if (!name) {
        return null;
      }

      const currentValue = normalizeNumber(variable.current_value ?? variable.currentValue);

      if (currentValue !== undefined) {
        return {
          name,
          textValue: String(currentValue),
        } satisfies VariableReplacement;
      }

      const contentPairs =
        (Array.isArray(variable.content_pairs)
          ? variable.content_pairs
          : Array.isArray(variable.contentPairs)
            ? variable.contentPairs
            : []) ?? [];

      const currentContentPairIndex =
        normalizeNumber(
          variable.current_content_pair_index ?? variable.currentContentPairIndex,
        ) ?? 0;
      const selectedPair = contentPairs[Math.max(0, currentContentPairIndex)];
      const variableContent = getContentSection(
        selectedPair?.variable_content ?? selectedPair?.variableContent,
      );

      if (!variableContent) {
        return null;
      }

      return {
        name,
        textValue: extractTextContent(variableContent),
      } satisfies VariableReplacement;
    })
    .filter((replacement): replacement is VariableReplacement => replacement !== null);

  return replacements.sort((left, right) => {
    if (left.name.length !== right.name.length) {
      return right.name.length - left.name.length;
    }

    return left.name.localeCompare(right.name);
  });
}

function replaceVariablesInText(text: string, replacements: VariableReplacement[]): string {
  return substituteVariablesInContent(
    text,
    replacements.map((replacement) => ({
      current_value: replacement.textValue,
      name: replacement.name,
    })),
  );
}

function hasMeaningfulHtmlContent(html: string | null) {
  if (!html) {
    return false;
  }

  if (typeof DOMParser !== 'undefined') {
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');

    const hasMeaningfulNode = (node: ChildNode): boolean => {
      if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent ?? '').replace(/\u00a0/g, ' ').trim().length > 0;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) {
        return false;
      }

      const element = node as Element;
      const tagName = element.tagName.toLowerCase();

      if (tagName === 'br') {
        return false;
      }

      if (
        tagName === 'img' ||
        tagName === 'iframe' ||
        tagName === 'video' ||
        tagName === 'audio' ||
        tagName === 'svg' ||
        tagName === 'math'
      ) {
        return true;
      }

      if (tagName === 'span' && normalizeString(element.getAttribute('data-value'))) {
        return true;
      }

      return Array.from(element.childNodes).some(hasMeaningfulNode);
    };

    return Array.from(documentFragment.body.childNodes).some(hasMeaningfulNode);
  }

  return html
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .trim().length > 0;
}

function extractTextContent(contentSection: RawContentSection): string {
  const contentUnits = toContentUnitArray(
    contentSection.content_units ?? contentSection.contentUnits,
  );

  return contentUnits
    .map((unit) => normalizeString(unit.text))
    .filter((text): text is string => Boolean(text))
    .join(' ')
    .trim();
}

function createBaseUnit(
  unit: RawContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
  replacements: VariableReplacement[],
) {
  const heading = getHeadingText(unit, replacements);
  const caption = getCaptionText(unit, replacements);
  const collapseByDefault =
    normalizeBoolean(unit.collapse_by_default ?? unit.collapseByDefault) ?? false;
  const disableAutoNumbering =
    normalizeBoolean(
      unit.disable_auto_numbering ?? unit.disableAutoNumbering,
    ) ?? false;

  let numberedHeading: string | undefined;

  if (heading) {
    numberedHeading = disableAutoNumbering
      ? heading
      : `${nextHeadingNumber()}. ${heading}`;
  }

  return {
    caption,
    collapseByDefault,
    disableAutoNumbering,
    heading,
    id: normalizeIdentifier(unit.id) ?? `question-content-unit-${unitPosition}`,
    numberedHeading,
    summaryLabel: getSummaryLabel(heading, numberedHeading, unitPosition),
  };
}

/**
 * Normalizes one question prompt unit.
 *
 * Delegates to the content feature's normalizer so every unit type behaves
 * identically in a question and in page content, then layers on the two things
 * that are specific to questions: variable substitution (##x## in prompt text)
 * and question-flavoured fallback titles.
 *
 * This used to be a parallel implementation with its own per-type branches, and
 * it had drifted — it knew TEXT, IMAGE and IFRAME but not TABLE, so a table in a
 * question prompt fell through to the video branch and was dropped without a
 * trace. Adding a branch here would have fixed tables and left the next unit
 * type to fail the same way.
 */
function normalizeQuestionContentUnit(
  unit: RawContentUnit,
  unitPosition: number,
  nextHeadingNumber: () => number,
  replacements: VariableReplacement[],
): NormalizedContentUnit | null {
  const normalized = normalizeContentUnit(unit, unitPosition, nextHeadingNumber);

  if (!normalized) {
    return null;
  }

  // Headings and captions carry variables too, and the shared normalizer knows
  // nothing about them.
  const heading = normalized.heading
    ? replaceVariablesInText(normalized.heading, replacements)
    : normalized.heading;
  const caption = normalized.caption
    ? replaceVariablesInText(normalized.caption, replacements)
    : normalized.caption;
  const numberedHeading = normalized.numberedHeading
    ? replaceVariablesInText(normalized.numberedHeading, replacements)
    : normalized.numberedHeading;
  const base = {
    ...normalized,
    caption,
    heading,
    id: normalized.id ?? `question-content-unit-${unitPosition}`,
    numberedHeading,
    summaryLabel: getSummaryLabel(heading, numberedHeading, unitPosition),
  };

  if (base.kind === 'text') {
    const html = replaceVariablesInText(base.html ?? '', replacements);

    // A prompt unit that renders to nothing is noise in a question, where the
    // surrounding chrome (number, points, answer field) already frames it.
    if (!hasMeaningfulHtmlContent(html) && !heading && !caption) {
      return null;
    }

    return { ...base, html };
  }

  if (base.kind === 'image') {
    return { ...base, alt: getImageAlt(heading, caption) };
  }

  if (base.kind === 'iframe') {
    return {
      ...base,
      title: heading ?? caption ?? 'Embedded question activity',
    };
  }

  if (base.kind === 'inlineVideo') {
    return {
      ...base,
      title: heading ?? caption ?? 'Embedded question video',
    };
  }

  // Everything else — tables today, whatever is added next — passes through
  // exactly as page content renders it.
  return base;
}

export function normalizeContentUnits(
  contentSection: RawContentSection | null | undefined,
  replacements: VariableReplacement[],
): NormalizedContentUnit[] {
  const contentUnits = toContentUnitArray(
    contentSection?.content_units ?? contentSection?.contentUnits,
  );
  let headingNumber = 0;

  return contentUnits
    .map((unit, index) =>
      normalizeQuestionContentUnit(unit, index + 1, () => {
        headingNumber += 1;
        return headingNumber;
      }, replacements),
    )
    .filter((unit): unit is NormalizedContentUnit => unit !== null);
}

function normalizePromptUnits(
  question: RawQuestion,
  replacements: VariableReplacement[],
): NormalizedContentUnit[] {
  const contentSection = getContentSection(
    question.content_section ?? question.contentSection,
  );
  const promptUnits = normalizeContentUnits(contentSection, replacements);

  if (promptUnits.length > 0) {
    return promptUnits;
  }

  const fallbackContent = normalizeString(question.content);

  if (!fallbackContent) {
    return [];
  }

  return [
    {
      caption: undefined,
      collapseByDefault: false,
      disableAutoNumbering: true,
      heading: undefined,
      html: replaceVariablesInText(fallbackContent, replacements),
      id: `question-content-${normalizeIdentifier(question.id) ?? 'unknown'}`,
      kind: 'text',
      numberedHeading: undefined,
      summaryLabel: 'Question content',
    } satisfies TextUnitData,
  ];
}

function getQuestionSettings(
  component: RawComponent,
  kind: QuestionSectionKind,
): RawQuestionSettings | null {
  // Resolves the practice flag across the two levels; a plain merge would let a
  // chapter's allow_ai_hints stand in for a practice value it chose to inherit.
  const mergeSettings = mergeAiQuestionSettings;

  if (kind === 'preLecture') {
    const preLectureSettings = isRecord(
      component.pre_lecture_question_settings ??
        component.preLectureQuestionSettings,
    )
      ? ((component.pre_lecture_question_settings ??
          component.preLectureQuestionSettings) as RawQuestionSettings)
      : null;
    const coursePreLectureSettings = isRecord(
      component.course_prelecture_question_settings ??
        component.coursePrelectureQuestionSettings,
    )
      ? ((component.course_prelecture_question_settings ??
          component.coursePrelectureQuestionSettings) as RawQuestionSettings)
      : null;

    return mergeSettings(coursePreLectureSettings, preLectureSettings);
  }

  const componentSettings = isRecord(
    component.question_settings ?? component.questionSettings,
  )
    ? ((component.question_settings ?? component.questionSettings) as RawQuestionSettings)
    : null;
  const courseSettings = isRecord(
    component.course_question_settings ?? component.courseQuestionSettings,
  )
    ? ((component.course_question_settings ?? component.courseQuestionSettings) as RawQuestionSettings)
    : null;

  return mergeSettings(courseSettings, componentSettings);
}

function getAssignedQuestionIndexes(
  rawQuestions: RawQuestion[],
  settings: RawQuestionSettings | null,
): Set<number> {
  const assignmentType = normalizeQuestionAssignmentType(settings?.type);

  if (assignmentType !== 'USER_DEFINED') {
    return new Set(rawQuestions.map((_, index) => index));
  }

  return new Set(
    toIntegerArray(
      settings?.user_defined_numbers ?? settings?.userDefinedNumbers,
    ),
  );
}

function resolveHintAttempts(
  questionType: RawQuestionType,
  settings: RawQuestionSettings | null,
): number | undefined {
  return readHintSetting(
    settings,
    questionType === 'MULTIPLE_CHOICE' || questionType === 'SELECT_ALL'
      ? 'multipleChoice'
      : 'exactAnswer',
  );
}

function resolveAllowAIHints(settings: RawQuestionSettings | null): boolean {
  return resolveAllowAiHints(settings);
}

function getOptionCount(question: RawQuestion): number {
  const contentOptions = Array.isArray(question.content_options)
    ? question.content_options
    : Array.isArray(question.contentOptions)
      ? question.contentOptions
      : [];

  if (contentOptions.length > 0) {
    return contentOptions.length;
  }

  return Array.isArray(question.options) ? question.options.length : 0;
}

// The chapter's own settings and its course's defaults for one kind of
// section, kept apart: an attempts value is read level by level.
function getSettingsLevels(
  component: RawComponent,
  kind: QuestionSectionKind,
): { chapter: RawQuestionSettings | null; course: RawQuestionSettings | null } {
  const chapter =
    kind === 'preLecture'
      ? component.pre_lecture_question_settings ?? component.preLectureQuestionSettings
      : component.question_settings ?? component.questionSettings;
  const course =
    kind === 'preLecture'
      ? component.course_prelecture_question_settings ??
        component.coursePrelectureQuestionSettings
      : component.course_question_settings ?? component.courseQuestionSettings;

  return {
    chapter: isRecord(chapter) ? (chapter as RawQuestionSettings) : null,
    course: isRecord(course) ? (course as RawQuestionSettings) : null,
  };
}

// A question's attempt limit, and whether its SETTING is single-attempt mode.
// The two differ for multiple choice: the option count can lower a limit to 1
// without making it Save only (see BaseQuestionData.isSingleAttemptMode).
function resolveAttemptLimit(
  question: RawQuestion,
  questionType: RawQuestionType,
  component: RawComponent,
  kind: QuestionSectionKind,
): { isSingleAttemptMode?: boolean; maxAttempts?: number } {
  const explicitAttempts = normalizeNumber(
    question.max_num_attempts ?? question.maxNumAttempts,
  );

  // AI uploads are the only type whose attempt limit is authored on the
  // question itself (the editor writes max_num_attempts, default 1).
  if (questionType === 'AI_UPLOAD') {
    return {
      maxAttempts:
        explicitAttempts !== undefined && explicitAttempts > 0
          ? explicitAttempts
          : 1,
    };
  }

  // For every other type the SECTION/COURSE settings are the only source of
  // truth, exactly like the legacy client. The question-level
  // use_custom_num_attempts / max_num_attempts fields are deliberately
  // IGNORED: the legacy settings dialog stamped them onto every question with
  // the then-current settings value, permanently freezing attempts (students
  // saw "Attempt 4 of 4" and a lock even after the instructor changed the
  // settings to Unlimited). The legacy client never honored the stamp (its
  // custom-attempts branch was commented out), and the current editor only
  // authors custom attempts for AI uploads. What a settings value means is in
  // attemptSettings.ts: nothing saved or 0 is 1 attempt, -1 and 100 are
  // unlimited.
  //
  // Select-all takes the multiple-choice attempt setting: it is authored
  // beside multiple choice and there is no separate setting for it. Without
  // this it fell through to `undefined` and got UNLIMITED attempts whatever
  // the instructor configured.
  const field =
    questionType === 'MULTIPLE_CHOICE' || questionType === 'SELECT_ALL'
      ? 'multipleChoice'
      : questionType === 'NUMERICAL_WITH_ERROR' || questionType === 'EXACT_ANSWER'
        ? 'exactAnswer'
        : undefined;

  if (!field) {
    return {};
  }

  const levels = getSettingsLevels(component, kind);
  const { limit } = resolveAttempts(levels.chapter, levels.course, field);

  // Unlimited is never capped. Capping it as the number it was stored as (100,
  // in the retired app) left an Unlimited question one attempt fewer than its
  // options (issue #54).
  if (limit === 'unlimited') {
    return { isSingleAttemptMode: false };
  }

  let maxAttempts = limit;

  // Deliberately NOT applied to select-all. The clamp exists because N-1 wrong
  // guesses identify the answer to an N-option single-select by elimination;
  // a select-all answer is one of 2^N subsets, so the same arithmetic would cap
  // attempts far below what the question actually gives away.
  if (questionType === 'MULTIPLE_CHOICE') {
    const optionCount = getOptionCount(question);

    if (optionCount > 0) {
      maxAttempts = Math.max(0, Math.min(maxAttempts, optionCount - 1));
    }
  }

  return {
    // Decided by the setting before the cap: a two-option question in a
    // chapter allowing 2 attempts keeps its Check, once.
    isSingleAttemptMode: limit === 1,
    maxAttempts: maxAttempts > 0 ? maxAttempts : undefined,
  };
}

function getAttemptsLabel(attemptsUsed: number, maxAttempts?: number) {
  return maxAttempts
    ? `Attempt ${attemptsUsed} of ${maxAttempts}`
    : `Attempt ${attemptsUsed} of unlimited`;
}

function getSectionStats(component: RawComponent, sectionId: string, index: number) {
  const sectionStats = toSectionStatsArray(
    component.section_stats ?? component.sectionStats,
  );

  return (
    sectionStats.find(
      (stats) => normalizeIdentifier(stats.id) === sectionId,
    ) ?? sectionStats[index] ?? null
  );
}

function getSectionStatsId(sectionStats: RawSectionStats | null, sectionId: string) {
  return normalizeIdentifier(sectionStats?.id) ?? sectionId;
}

function getSectionNumAttempts(
  sectionStats: RawSectionStats | null,
  kind: QuestionSectionKind,
) {
  return (
    normalizeNumber(
      kind === 'preLecture'
        ? sectionStats?.num_pre_lecture_attempts ??
            sectionStats?.numPreLectureAttempts
        : sectionStats?.num_attempts ?? sectionStats?.numAttempts,
    ) ?? 0
  );
}

function getRawQuestionStats(
  sectionStats: RawSectionStats | null,
  kind: QuestionSectionKind,
) {
  return toQuestionStatsArray(
    kind === 'preLecture'
      ? sectionStats?.pre_lecture_question_stats ??
          sectionStats?.preLectureQuestionStats
      : sectionStats?.question_stats ?? sectionStats?.questionStats,
  );
}

function getQuestionStatsFromSet(questionSetStats: RawQuestionSetStats | null | undefined) {
  return toQuestionStatsArray(
    questionSetStats?.question_stats ?? questionSetStats?.questionStats,
  );
}

function getQuestionStat(
  sectionStats: RawSectionStats | null,
  question: RawQuestion,
  index: number,
  kind: QuestionSectionKind,
): RawQuestionStats | null {
  if (!sectionStats) {
    return null;
  }

  const questionStats = getRawQuestionStats(sectionStats, kind);
  const questionId =
    normalizeIdentifier(question.id) ?? normalizeIdentifier(question.uid);

  if (questionId) {
    const matched = questionStats.find(
      (stats) => normalizeIdentifier(stats.id) === questionId,
    );

    if (matched) {
      return matched;
    }
  }

  return questionStats[index] ?? null;
}

function getNestedQuestionStat(
  questionStats: RawQuestionStats | null,
  question: RawQuestion,
  index: number,
): RawQuestionStats | null {
  if (!questionStats) {
    return null;
  }

  const nestedStats = getQuestionStatsFromSet(
    questionStats.question_set_stats ?? questionStats.questionSetStats,
  );
  const questionId =
    normalizeIdentifier(question.id) ?? normalizeIdentifier(question.uid);

  if (questionId) {
    const matched = nestedStats.find(
      (stats) => normalizeIdentifier(stats.id) === questionId,
    );

    if (matched) {
      return matched;
    }
  }

  return nestedStats[index] ?? null;
}

// The viewer's answer is the one in THEIR record, and nowhere else.
//
// A question also has a `current_answer` field of its own, and this used to
// fall back to it when the viewer had no answer. But the question is the
// shared definition every viewer is served: the retired frontend kept a
// viewer's selection there and saved it with the chapter, so stored chapters
// can hold one user's picks on every question. Read as the viewer's, they
// arrived pre-selected and marked Saved for anyone with no record — and then
// all vanished on the viewer's first save, which gives them an (empty) entry
// for every question in the section. See storedSelectionReplication.test.ts.
function getCurrentAnswer(questionStats: RawQuestionStats | null) {
  return isRecord(questionStats?.answer)
    ? (questionStats?.answer as RawAnswer)
    : null;
}

function getQuestionVariables(question: RawQuestion): RawVariable[] {
  return Array.isArray(question.variables) ? question.variables : [];
}

// The editor authors a multi-part question's random variables on the PARENT —
// the variables panel edits the parent, and parts carry only
// variable_based_answer plus ##var## placeholders. So whenever the parent
// defines variables it is the SINGLE SOURCE OF TRUTH for every part, and a
// part's own array is ignored: older saves passed a part's stored variables
// through untouched, so editing the ranges updated the parent while every part
// kept the OLD ranges — the stem then showed one value while the parts' answers
// were computed from another. Sharing the parent's array also upholds the
// multi-part seeding contract (every part seeds from the user id alone, so the
// stem and all parts substitute the SAME values).
//
// A parent with NO variables is legacy per-part authoring: those parts keep
// their own arrays. An explicit use_random_variables: false on a part still
// opts that part out entirely.
function withInheritedRandomVariables(
  part: RawQuestion,
  parentVariables: RawVariable[],
): RawQuestion {
  if (parentVariables.length === 0) {
    return part;
  }

  const partFlag = normalizeBoolean(
    part.use_random_variables ?? part.useRandomVariables,
  );

  if (partFlag === false) {
    return part;
  }

  return {
    ...part,
    use_random_variables: true,
    variables: parentVariables,
  };
}

function getAnswerIndexStats(
  question: RawQuestion,
  questionStats: RawQuestionStats | null,
) {
  const statsAnswer = isRecord(questionStats?.answer)
    ? (questionStats?.answer as RawAnswer)
    : null;

  return toNumberArray(
    statsAnswer?.answer_index_stats ??
      statsAnswer?.answerIndexStats ??
      question.answer_index_stats ??
      question.answerIndexStats,
  );
}

function hasResolvedVariableState(variables: RawVariable[]) {
  return variables.length > 0 && variables.every((variable) => {
    const contentPairs = Array.isArray(variable.content_pairs)
      ? variable.content_pairs
      : Array.isArray(variable.contentPairs)
        ? variable.contentPairs
        : [];

    if (contentPairs.length > 0) {
      return normalizeNumber(
        variable.current_content_pair_index ?? variable.currentContentPairIndex,
      ) !== undefined;
    }

    return normalizeNumber(variable.current_value ?? variable.currentValue) !== undefined;
  });
}

// As getCurrentAnswer: the viewer's own entry only. `current_num_attempts` on
// a question is somebody's count, stored on the definition everyone shares.
function getAttemptsUsed(questionStats: RawQuestionStats | null) {
  return (
    normalizeNumber(
      questionStats?.current_num_attempts ?? questionStats?.currentNumAttempts,
    ) ?? 0
  );
}

function getPointsPossible(question: RawQuestion, fallback = 1) {
  const value = normalizeNumber(question.value);

  return value !== undefined && value > 0 ? value : fallback;
}

function getSelectedIndex(answer: RawAnswer | null) {
  const answerIndex =
    (Array.isArray(answer?.answer_index)
      ? answer?.answer_index
      : Array.isArray(answer?.answerIndex)
        ? answer?.answerIndex
        : []) ?? [];

  // The backend serializes answer_index as strings in JSON (like its sibling
  // score/numerical_answer/answer_index_stats fields), so parse leniently rather
  // than requiring a JS number — a strict typeof check silently dropped saved
  // multiple-choice answers, leaving them shown as "No answer submitted" and
  // uncounted in progress even though they scored. Same first-element + >= 0
  // semantics as before.
  const selectedIndex = normalizeNumber(answerIndex[0]);
  return selectedIndex !== undefined && selectedIndex >= 0
    ? Math.trunc(selectedIndex)
    : undefined;
}

// Every correct index, for SELECT_ALL. getCorrectAnswerIndex below truncates to
// the first, which is right for multiple choice and silently wrong here.
function getCorrectAnswerIndices(question: RawQuestion) {
  return toIntegerArray(question.answer_index ?? question.answerIndex);
}

// Every selected index, for SELECT_ALL. getSelectedIndex returns only the first.
function getSelectedIndices(answer: RawAnswer | null) {
  const answerIndex =
    (Array.isArray(answer?.answer_index)
      ? answer?.answer_index
      : Array.isArray(answer?.answerIndex)
        ? answer?.answerIndex
        : []) ?? [];

  return toIntegerArray(answerIndex).filter((index) => index >= 0);
}

function getCorrectAnswerIndex(question: RawQuestion) {
  const indexes = toIntegerArray(question.answer_index ?? question.answerIndex);
  return indexes[0];
}

function getNumericalInputValue(answer: RawAnswer | null) {
  const baseValue =
    normalizeNumber(answer?.numerical_answer ?? answer?.numericalAnswer);
  const exponent =
    normalizeNumber(
      answer?.numerical_answer_extra_exponent ??
        answer?.numericalAnswerExtraExponent,
    );

  if (baseValue === undefined) {
    return '';
  }

  // An exponent of zero is no exponent — rendering "42e0" would put a stored
  // shape in front of the student instead of their answer.
  return exponent !== undefined && exponent !== 0
    ? `${baseValue}e${exponent}`
    : String(baseValue);
}

function getExactInputValue(answer: RawAnswer | null) {
  return normalizeString(answer?.exact_answer ?? answer?.exactAnswer) ?? '';
}

function getFileUploadUrl(answer: RawAnswer | null) {
  return normalizeString(answer?.file_upload_url ?? answer?.fileUploadUrl) ?? undefined;
}

// Whether an answer holds something the viewer actually entered.
//
// From a viewer's first save on, their record has an entry for EVERY question
// in the section, and the ones they have not answered hold an empty answer
// (`{}`, or `answer_index: [-1]` in records written by older clients, or only
// the per-user random values). Those entries are not answers. Counting them as
// answers made every unanswered question read "Answer saved" the moment
// another question was saved, and that change of status is what made the
// question cards throw away options the student had picked but not yet saved.
//
// The same test as answerHasStudentResponse in
// backend/app/course-student-merge.go, which decides what a save may overwrite.
function hasStudentResponse(answer: RawAnswer | null) {
  if (!answer) {
    return false;
  }

  return (
    getSelectedIndices(answer).length > 0 ||
    getExactInputValue(answer).length > 0 ||
    getNumericalInputValue(answer).length > 0 ||
    getFileUploadUrl(answer) !== undefined ||
    normalizeBoolean(
      answer.credit_given_manually ?? answer.creditGivenManually,
    ) === true
  );
}

// The same for a question's entry as a whole. A multi-part question keeps an
// empty answer of its own and the real ones on its parts, so it has been
// answered when any part has.
function hasAnsweredQuestion(questionStats: RawQuestionStats | null): boolean {
  if (!questionStats) {
    return false;
  }

  return (
    hasStudentResponse(getCurrentAnswer(questionStats)) ||
    getQuestionStatsFromSet(
      questionStats.question_set_stats ?? questionStats.questionSetStats,
    ).some((partStats) => hasAnsweredQuestion(partStats))
  );
}

function getFileNameFromUrl(url: string | undefined) {
  if (!url) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(url);
    const rawPath = parsedUrl.pathname.split('/o/')[1] ?? parsedUrl.pathname;
    const decodedPath = decodeURIComponent(rawPath);
    const [fileName] = decodedPath.split('/').reverse();
    return fileName?.replace(/^kudu_\d+_/, '') || 'Submitted file';
  } catch {
    const [fileName] = url.split('/').reverse();
    return fileName || 'Submitted file';
  }
}

function getResolvedRandomExactAnswer(answer: RawAnswer | null) {
  return (
    normalizeString(
      answer?.current_random_exact_answer ?? answer?.currentRandomExactAnswer,
    ) ?? undefined
  );
}

/*
 * The correct answer to SHOW, which is not always stored anywhere.
 *
 * For a randomized question the answer is a formula, so there is no authored
 * value to fall back on, and the stored per-student answer only exists once a
 * student has been served the question. An instructor therefore had no correct
 * answer at all: the resolver computed one from the representative values — the
 * same numbers shown in the prompt — and these read straight past it, leaving
 * the "✓ Correct answer" line to be dropped while the tolerance beside it
 * rendered. The sibling fields in the same return blocks
 * (currentRandomNumericalAnswer, currentRandomExactAnswer) already took the
 * resolved value; only these did not.
 *
 * Whose answer the resolved value is depends on who is asking, and that is
 * decided before it reaches here: representative resolution requires
 * isInstructorRequest, and a student's resolution is seeded from their own user
 * id. So this can hand a student their own answer, never anyone else's, and
 * only where the caller has already decided the answer may be revealed.
 */
function getCorrectExactAnswer(
  question: RawQuestion,
  answer: RawAnswer | null,
  resolvedRandomVariables?: ReturnType<typeof resolveRandomVariables>,
) {
  return (
    getResolvedRandomExactAnswer(answer) ??
    normalizeString(resolvedRandomVariables?.correctAnswer.exact) ??
    normalizeString(question.exact_answer ?? question.exactAnswer) ??
    undefined
  );
}

function getCorrectNumericalAnswer(
  question: RawQuestion,
  answer: RawAnswer | null,
  resolvedRandomVariables?: ReturnType<typeof resolveRandomVariables>,
) {
  return (
    getResolvedRandomNumericalAnswer(answer) ??
    resolvedRandomVariables?.correctAnswer.numerical ??
    normalizeNumber(question.numerical_answer ?? question.numericalAnswer)
  );
}

function getCorrectNumericalExponent(
  question: RawQuestion,
  answer: RawAnswer | null,
  resolvedRandomVariables?: ReturnType<typeof resolveRandomVariables>,
) {
  const exponent =
    getResolvedRandomNumericalExponent(answer) ??
    resolvedRandomVariables?.correctAnswer.extraExponent ??
    normalizeNumber(
      question.numerical_answer_extra_exponent ??
        question.numericalAnswerExtraExponent,
    );

  // Same rule as the student's own answer: zero means the value stands alone,
  // so the correct answer is shown as "42" rather than "42e0".
  return exponent === 0 ? undefined : exponent;
}

function getResolvedRandomNumericalAnswer(answer: RawAnswer | null) {
  return normalizeNumber(
    answer?.current_random_numerical_answer ??
      answer?.currentRandomNumericalAnswer,
  );
}

function getResolvedRandomNumericalExponent(answer: RawAnswer | null) {
  return normalizeNumber(
    answer?.current_random_numerical_answer_extra_exponent ??
      answer?.currentRandomNumericalAnswerExtraExponent,
  );
}

function hasBackendResolvedRandomAnswer(answer: RawAnswer | null) {
  return (
    getResolvedRandomExactAnswer(answer) !== undefined ||
    getResolvedRandomNumericalAnswer(answer) !== undefined
  );
}

// The variable values the student saw, persisted alongside their answer (see
// Answer.variables). Reduced to name + current_value / current_content_pair_index
// — the display re-hydrates min/max/content_pairs from the shared question — to
// keep the stored answer small.
function toPersistedRandomVariables(variables: RawVariable[]): RawVariable[] {
  return variables.map((variable) => {
    const name = normalizeString(variable.name ?? undefined);
    const currentValue = normalizeNumber(
      variable.current_value ?? variable.currentValue,
    );
    const currentContentPairIndex = normalizeNumber(
      variable.current_content_pair_index ?? variable.currentContentPairIndex,
    );

    return {
      ...(name !== null ? { name } : {}),
      ...(currentValue !== undefined ? { current_value: currentValue } : {}),
      ...(currentContentPairIndex !== undefined
        ? { current_content_pair_index: currentContentPairIndex }
        : {}),
    } satisfies RawVariable;
  });
}

// Merge the student's PERSISTED variable values (from Answer.variables) onto the
// question's authored variables, matched by name. Only adopted when the stored
// set covers EVERY variable — a partial set (e.g. the question later gained a
// variable) falls back to the authored variables so the client re-resolves from
// the seed. Making every variable carry a current value is what lets
// hasResolvedVariableState short-circuit re-resolution so the student sees the
// same numbers their stored answer was graded against.
function applyStoredVariableValues(
  questionVariables: RawVariable[],
  answer: RawAnswer | null,
): { variables: RawVariable[]; injected: boolean } {
  const storedVariables = Array.isArray(answer?.variables)
    ? answer.variables
    : [];

  if (questionVariables.length === 0 || storedVariables.length === 0) {
    return { variables: questionVariables, injected: false };
  }

  const storedByName = new Map<string, RawVariable>();
  storedVariables.forEach((stored) => {
    const name = normalizeString(stored?.name ?? undefined);
    if (name !== null) {
      storedByName.set(name, stored);
    }
  });

  const merged = questionVariables.map((variable) => {
    const name = normalizeString(variable.name ?? undefined);
    const stored = name !== null ? storedByName.get(name) : undefined;

    if (!stored) {
      return variable;
    }

    const currentValue = normalizeNumber(
      stored.current_value ?? stored.currentValue,
    );
    const currentContentPairIndex = normalizeNumber(
      stored.current_content_pair_index ?? stored.currentContentPairIndex,
    );

    return {
      ...variable,
      ...(currentValue !== undefined
        ? { current_value: currentValue, currentValue }
        : {}),
      ...(currentContentPairIndex !== undefined
        ? {
            current_content_pair_index: currentContentPairIndex,
            currentContentPairIndex,
          }
        : {}),
    } satisfies RawVariable;
  });

  return hasResolvedVariableState(merged)
    ? { variables: merged, injected: true }
    : { variables: questionVariables, injected: false };
}

// Choose the numerical random answer to display AND persist for a randomized
// question, together with the variables that produce it — always a matched pair.
//
//   - A student who already earned this question's credit keeps their graded
//     answer untouched (never regressed by a later prompt/seed edit).
//   - Everyone else is aligned to the variables currently shown to them: when
//     the client re-resolved (no usable stored variables), that is the fresh
//     answer + fresh variables; otherwise the stored variables already drove the
//     display and match the stored answer.
//
// The variables are persisted so a wholesale answer replace on the next save can
// never desync display from grading; a credited legacy answer with no stored
// variables persists none (there are no variables that match it).
function resolveNumericalRandomAnswer(args: {
  answer: RawAnswer | null;
  resolvedRandomVariables: ReturnType<typeof resolveRandomVariables> | undefined;
  questionVariables: RawVariable[];
  storedVariablesWereInjected: boolean;
  studentHasCredit: boolean;
}): {
  currentRandomNumericalAnswer: number | undefined;
  currentRandomNumericalAnswerExtraExponent: number | undefined;
  currentRandomVariables: RawVariable[] | undefined;
} {
  const {
    answer,
    resolvedRandomVariables,
    questionVariables,
    storedVariablesWereInjected,
    studentHasCredit,
  } = args;

  const storedNumerical = getResolvedRandomNumericalAnswer(answer);
  const storedExponent = getResolvedRandomNumericalExponent(answer);
  const freshNumerical = resolvedRandomVariables?.correctAnswer.numerical;
  const freshExponent = resolvedRandomVariables?.correctAnswer.extraExponent;

  const useFresh = studentHasCredit
    ? storedNumerical === undefined && freshNumerical !== undefined
    : freshNumerical !== undefined;

  const currentRandomNumericalAnswer = useFresh ? freshNumerical : storedNumerical;
  const currentRandomNumericalAnswerExtraExponent = useFresh
    ? freshExponent
    : storedExponent;

  let currentRandomVariables: RawVariable[] | undefined;
  if (useFresh && resolvedRandomVariables) {
    currentRandomVariables = toPersistedRandomVariables(
      resolvedRandomVariables.variables,
    );
  } else if (!useFresh && storedVariablesWereInjected) {
    currentRandomVariables = toPersistedRandomVariables(questionVariables);
  }

  return {
    currentRandomNumericalAnswer,
    currentRandomNumericalAnswerExtraExponent,
    currentRandomVariables,
  };
}

function getScore(questionStats: RawQuestionStats | null) {
  return normalizeNumber(questionStats?.score);
}

function normalizeGradingDetails(
  questionStats: RawQuestionStats | null,
): NormalizedGradingDetails | undefined {
  const rawDetails = questionStats?.grading_details ?? questionStats?.gradingDetails;

  if (!isRecord(rawDetails)) {
    return undefined;
  }

  const feedback = normalizeString(rawDetails.feedback) ?? undefined;
  const manualFeedback =
    normalizeString(rawDetails.manual_feedback ?? rawDetails.manualFeedback) ??
    undefined;
  const uploadFileUrl =
    normalizeString(rawDetails.upload_file_url ?? rawDetails.uploadFileUrl) ??
    undefined;

  if (!feedback && !manualFeedback && !uploadFileUrl) {
    return undefined;
  }

  return {
    feedback,
    manualFeedback,
    uploadFileUrl,
  };
}

/**
 * The backend's verdict on the submitted answer, independent of the score.
 *
 * Undefined means the question has not been graded — which is NOT the same as
 * graded and wrong, and must not be reported as either.
 */
function getAnsweredCorrectly(questionStats: RawQuestionStats | null) {
  if (!questionStats) {
    return undefined;
  }

  return (
    normalizeBoolean(
      questionStats.answered_correctly ?? questionStats.answeredCorrectly,
    ) ?? undefined
  );
}

function getParticipationWeight(settings: RawQuestionSettings | null) {
  const weight = normalizeNumber(
    settings?.all_questions_participation_credit_weight ??
      settings?.allQuestionsParticipationCreditWeight,
  );

  return weight ?? 1;
}

function createFeedback(
  question: RawQuestion,
  questionType: RawQuestionType,
  questionStats: RawQuestionStats | null,
  pointsPossible: number,
  isParticipationOnly: boolean,
  settings: RawQuestionSettings | null,
  // Single-attempt questions have no Check and are not designed to reveal
  // correctness from a save until the due date has passed. When true, an answered
  // question is reported as a neutral "saved" answer even if the backend scored it.
  suppressCorrectness = false,
): NormalizedAnswerFeedbackData | undefined {
  const score = getScore(questionStats);
  // Has the viewer answered? An entry with an empty answer has not (see
  // hasStudentResponse): it must give the same feedback as no entry at all.
  const answer = hasAnsweredQuestion(questionStats);
  const isSaved =
    normalizeBoolean(questionStats?.save_only ?? questionStats?.saveOnly) ?? false;

  // A saved answer (save_only), or a single-attempt answer before the due date,
  // is neutral: no correct/incorrect indication regardless of any stored score.
  // Checked FIRST, and before participation, because a student who only saved
  // has not asked to be told anything — participation credit changes what a
  // question is worth, not when its answer is revealed.
  if (isSaved || (suppressCorrectness && (score !== undefined || answer))) {
    return {
      description: 'Your answer is saved.',
      earnedPoints: undefined,
      possiblePoints: pointsPossible,
      status: 'saved',
      title: 'Answer saved',
    };
  }

  if (isParticipationOnly && (score !== undefined || answer)) {
    const weight = getParticipationWeight(settings);
    const participationPoints = pointsPossible > 0 ? pointsPossible * weight : score;
    const pointsSentence =
      participationPoints !== undefined
        ? `${formatPoints(participationPoints, pointsPossible)} awarded for participation.`
        : 'Credit awarded for participation.';
    // Participation credit decides what the question is WORTH, not whether the
    // student got it right, and the score cannot carry the verdict because it is
    // full either way. The backend records correctness separately; when it has
    // (an ungraded answer leaves it unset) the student is told both, because
    // being marked right or wrong is the entire reason to press Check.
    const answeredCorrectly = getAnsweredCorrectly(questionStats);

    if (answeredCorrectly !== undefined) {
      return {
        description: answeredCorrectly
          ? `Your answer is correct. ${pointsSentence}`
          : `Your answer is incorrect. ${pointsSentence}`,
        earnedPoints: participationPoints,
        possiblePoints: pointsPossible,
        status: answeredCorrectly ? 'correct' : 'incorrect',
        title: answeredCorrectly ? 'Correct' : 'Incorrect',
      };
    }

    return {
      description: pointsSentence,
      earnedPoints: participationPoints,
      possiblePoints: pointsPossible,
      status: 'participation',
      title: 'Participation credit',
    };
  }

  if (score === undefined) {
    return undefined;
  }

  // Hybrid participation mode ("one point for answering + one for correctness"):
  // participation credit is OFF but a positive participation weight is set. The
  // backend awards pointsPossible×weight for ANY answer and adds the correctness
  // points on top, so a raw score can equal pointsPossible without the answer
  // being correct. Correctness MUST therefore be judged against
  // participationAward + pointsPossible, never against pointsPossible alone.
  // Read the RAW weight here — getParticipationWeight defaults an unset weight to
  // 1 (a participation-only concern), which would wrongly flag every question as
  // hybrid. (The pointsPossible > 0 guard excludes value-less MULTI_PART parents,
  // whose parts carry their own feedback.)
  const hybridWeight = normalizeNumber(
    settings?.all_questions_participation_credit_weight ??
      settings?.allQuestionsParticipationCreditWeight,
  );
  if (
    !isParticipationOnly &&
    hybridWeight !== undefined &&
    hybridWeight > 0 &&
    pointsPossible > 0
  ) {
    const participationAward = pointsPossible * hybridWeight;
    const totalPossible = pointsPossible + participationAward;

    if (score >= totalPossible) {
      return {
        description:
          questionType === 'MULTIPLE_CHOICE' ? undefined : 'Your answer is correct.',
        earnedPoints: score,
        possiblePoints: totalPossible,
        status: 'correct',
        title: formatPoints(score, totalPossible),
      };
    }

    if (score > participationAward) {
      return {
        description: 'Your answer earned partial credit.',
        earnedPoints: score,
        possiblePoints: totalPossible,
        status: 'partial',
        title: formatPoints(score, totalPossible),
      };
    }

    return {
      description:
        score > 0
          ? 'Incorrect — participation credit awarded.'
          : questionType === 'MULTIPLE_CHOICE'
            ? undefined
            : 'Your answer is incorrect.',
      earnedPoints: score,
      possiblePoints: totalPossible,
      status: 'incorrect',
      title: formatPoints(score, totalPossible),
    };
  }

  if (pointsPossible > 0 && score >= pointsPossible) {
    return {
      description:
        questionType === 'MULTIPLE_CHOICE' ? undefined : 'Your answer is correct.',
      earnedPoints: score,
      possiblePoints: pointsPossible,
      status: 'correct',
      title: formatPoints(score, pointsPossible),
    };
  }

  if (score <= 0) {
    return {
      description:
        questionType === 'MULTIPLE_CHOICE' ? undefined : 'Your answer is incorrect.',
      earnedPoints: score,
      possiblePoints: pointsPossible,
      status: 'incorrect',
      title: formatPoints(score, pointsPossible),
    };
  }

  return {
    description: 'Your answer earned partial credit.',
    earnedPoints: score,
    possiblePoints: pointsPossible,
    status: 'partial',
    title: formatPoints(score, pointsPossible),
  };
}

function formatPoints(earnedPoints: number, possiblePoints: number) {
  const format = (value: number) => {
    if (Number.isInteger(value)) {
      return String(value);
    }

    return value.toFixed(2).replace(/\.?0+$/, '');
  };

  return `${format(earnedPoints)}/${format(possiblePoints)} points`;
}

function shouldShowHint(
  questionType: RawQuestionType,
  question: RawQuestion,
  attemptsUsed: number,
  settings: RawQuestionSettings | null,
  isPastDue: boolean,
  solutionVisible: boolean,
) {
  const hintContent = getContentSection(question.hint);

  if (!hintContent) {
    return false;
  }

  const hintSetting = resolveHintAttempts(questionType, settings);

  if (hintSetting === -1) {
    return true;
  }

  const canShowInCurrentWindow = !isPastDue || solutionVisible;

  if (hintSetting !== undefined && hintSetting > 0) {
    return canShowInCurrentWindow && attemptsUsed >= hintSetting;
  }

  // 0 is what the settings dialogs save for "Only with the solutions", and an
  // unset setting reads the same: the hint shows with the solution. 0 used to hide
  // the hint for good, so no such hint ever showed (issue #92).
  return solutionVisible;
}

function shouldShowSolution(
  question: RawQuestion,
  component: RawComponent,
  settings: RawQuestionSettings | null,
  now: number,
) {
  const detailedAnswer = getContentSection(
    question.detailed_answer ?? question.detailedAnswer,
  );

  if (!detailedAnswer) {
    return false;
  }

  const setting = readSolutionsSetting(settings);

  if (setting === 4) {
    return false;
  }

  const dueTimestamp = getDueTimestamp(component);

  if (!dueTimestamp) {
    return false;
  }

  const visibilityThreshold =
    setting === 1
      ? dueTimestamp + 60 * 60 * 1000
      : setting === 2
        ? dueTimestamp + 24 * 60 * 60 * 1000
        : setting === 3
          ? dueTimestamp + 7 * 24 * 60 * 60 * 1000
          : dueTimestamp;

  return now >= visibilityThreshold;
}

function createDisclosure(
  summaryLabel: string,
  contentSection: RawContentSection | null | undefined,
  isVisible: boolean,
  replacements: VariableReplacement[],
): NormalizedDisclosureData | undefined {
  if (!contentSection) {
    return undefined;
  }

  const units = normalizeContentUnits(contentSection, replacements);

  if (units.length === 0) {
    return undefined;
  }

  return {
    isVisible,
    summaryLabel,
    units,
  };
}

function isParticipationOnly(question: RawQuestion, settings: RawQuestionSettings | null) {
  return (
    (normalizeBoolean(
      question.credit_for_participation ?? question.creditForParticipation,
    ) ?? false) ||
    (normalizeBoolean(
      settings?.all_questions_participation_credit ??
        settings?.allQuestionsParticipationCredit,
    ) ?? false)
  );
}

function normalizeMultipleChoiceOptions(
  question: RawQuestion,
  replacements: VariableReplacement[],
  generatedOptions?: RawContentSection[] | null,
): NormalizedMultipleChoiceOption[] {
  const richOptions = generatedOptions ??
    (Array.isArray(question.content_options)
      ? question.content_options
      : Array.isArray(question.contentOptions)
        ? question.contentOptions
        : []);

  if (richOptions.length > 0) {
    return richOptions.map((option, index) => {
      const units = normalizeContentUnits(option, replacements);

      return {
        contentUnits: units,
        id: `option-${normalizeIdentifier(question.id) ?? 'question'}-${index}`,
        index,
        label:
          extractTextContent(option) ||
          `Option ${index + 1}`,
      };
    });
  }

  return toStringArray(question.options).map((option, index) => ({
    id: `option-${normalizeIdentifier(question.id) ?? 'question'}-${index}`,
    index,
    label: option,
    text: replaceVariablesInText(option, replacements),
  }));
}

function normalizeGradingRubric(
  question: RawQuestion,
  questionId: string,
): NormalizedGradingRubric {
  const rawRubric = question.grading_rubric ?? question.gradingRubric;
  const rawUnits = isRecord(rawRubric)
    ? rawRubric.rubric_units ?? rawRubric.rubricUnits
    : undefined;
  const rubricUnits = Array.isArray(rawUnits)
    ? rawUnits
        .filter(isRecord)
        .map((unit, index): NormalizedRubricUnit => ({
          details: normalizeString(unit.details) ?? '',
          id:
            normalizeIdentifier(unit.id) ??
            `${questionId}-rubric-unit-${index + 1}`,
          name: normalizeString(unit.name) ?? `Rubric unit ${index + 1}`,
          score: normalizeNumber(unit.score) ?? 0,
        }))
    : [];
  const fallbackUnits =
    rubricUnits.length > 0
      ? rubricUnits
      : [
          {
            details: '',
            id: `${questionId}-rubric-unit-1`,
            name: 'Correctness',
            score: getPointsPossible(question, 1),
          },
        ];
  const totalScore =
    (isRecord(rawRubric)
      ? normalizeNumber(rawRubric.total_score ?? rawRubric.totalScore)
      : undefined) ??
    fallbackUnits.reduce((total, unit) => total + unit.score, 0);

  return {
    rubricUnits: fallbackUnits,
    totalScore,
  };
}

function normalizeQuestionInternal(
  question: RawQuestion,
  component: RawComponent,
  kind: QuestionSectionKind,
  sectionId: string,
  questionSectionId: string,
  questionIndex: number,
  questionStats: RawQuestionStats | null,
  sectionHasAggregateStats: boolean,
  now: number,
  isAssigned: boolean,
  isInstructorRequest: boolean,
  timingState: ComponentTimingState | undefined,
  kuduUserId?: string,
  partLabel?: string,
  parentQuestionId?: string,
): NormalizedQuestionData | null {
  const questionType = normalizeQuestionType(question.type);

  if (!questionType) {
    console.warn('Skipping unsupported question type', {
      id: question.id,
      type: question.type,
    });
    return null;
  }

  const settings = getQuestionSettings(component, kind);
  // Staff answer nothing: an instructor's record holds the class statistics,
  // and one stored before the server kept only averages there carries a
  // student's answer, which the page drew as the instructor's own (issue #174).
  // The option shares are read from the stats themselves (getAnswerIndexStats).
  const answer = isInstructorRequest ? null : getCurrentAnswer(questionStats);
  // Re-hydrate the student's persisted variable values (Answer.variables) onto
  // the authored variables so display and grading use the exact numbers their
  // answer was graded against. When the stored set fully covers the variables,
  // `injected` is true and the re-resolution gate below is suppressed.
  const { variables: questionVariables, injected: storedVariablesWereInjected } =
    applyStoredVariableValues(getQuestionVariables(question), answer);
  // When use_random_variables is ABSENT (not explicitly false), infer it from the
  // presence of authored variables. The editor's multi-part serializer used to
  // strip the flag from parts on save, leaving stored parts with variables (and
  // ##var## placeholders) but no flag — without the inference those parts render
  // raw "##x##" to students. An explicit false still disables randomization.
  const usesRandomVariables =
    questionType === 'AI_UPLOAD'
      ? false
      : normalizeBoolean(
          question.use_random_variables ?? question.useRandomVariables,
        ) ?? questionVariables.length > 0;
  // Instructors preview a deterministic REPRESENTATIVE set of values (not a
  // specific student's), so authored `##VAR##` placeholders are always
  // substituted before they reach the KaTeX parser — and a note tells the
  // instructor each student sees their own numbers. Students keep their per-user
  // resolution. (Edit mode renders via normalizeEditableQuestionForDisplay and is
  // unaffected — it intentionally keeps the raw `##VAR##` notation.)
  const showRepresentativeVariableValues =
    usesRandomVariables && isInstructorRequest && questionVariables.length > 0;
  const shouldResolveClientRandomVariables =
    !showRepresentativeVariableValues &&
    usesRandomVariables &&
    Boolean(kuduUserId) &&
    (questionType === 'MULTIPLE_CHOICE' ||
      !hasBackendResolvedRandomAnswer(answer) ||
      !hasResolvedVariableState(questionVariables));
  let resolvedRandomVariables:
    | ReturnType<typeof resolveRandomVariables>
    | undefined;

  // Multi-part questions seed from the user id alone — for the parts AND the
  // parent stem — so the stem and every part substitute the SAME values for the
  // shared variable set (matching the legacy client's seeding).
  const usesMultiPartSeed =
    questionType === 'MULTI_PART' || Boolean(parentQuestionId);

  if (showRepresentativeVariableValues) {
    try {
      resolvedRandomVariables = resolveRepresentativeRandomVariables(
        question,
        usesMultiPartSeed,
      );
    } catch (error) {
      console.warn('Unable to resolve representative question variables', {
        error,
        id: question.id,
      });
    }
  } else if (shouldResolveClientRandomVariables && kuduUserId) {
    try {
      resolvedRandomVariables = resolveRandomVariables(
        question,
        kuduUserId,
        usesMultiPartSeed,
      );
    } catch (error) {
      console.warn('Unable to resolve random question variables', {
        error,
        id: question.id,
      });
    }
  }

  const resolvedVariables =
    resolvedRandomVariables?.variables.length
      ? resolvedRandomVariables.variables
      : questionVariables;
  const usesRepresentativeVariableValues =
    showRepresentativeVariableValues &&
    Boolean(resolvedRandomVariables?.variables.length);
  const replacements = buildVariableReplacements(resolvedVariables);
  const promptUnits = normalizePromptUnits(question, replacements);
  const imagePaths = toStringArray(question.image_paths ?? question.imagePaths);
  const attemptsUsed = getAttemptsUsed(questionStats);
  const { isSingleAttemptMode, maxAttempts } = resolveAttemptLimit(
    question,
    questionType,
    component,
    kind,
  );
  // The AI hint threshold is now its own setting, independent of the authored
  // hint threshold below. Previously these were the same number, so the
  // authored-hint default of 0 silently hid AI hints everywhere.
  const aiHintThreshold = resolveAiHintThreshold(settings);
  const hintSetting = resolveHintAttempts(questionType, settings);
  // A single-attempt objective question (multiple choice / numerical / exact
  // answer) has no Check button; its answer is a save the student may revise until
  // the due date. The one consumed attempt must NOT lock it — `isReviewMode` (past
  // due) gates canSave/canCheck instead. AI_UPLOAD and MULTI_PART are excluded:
  // those flows are designed to surface grading feedback on submission.
  //
  // Single-attempt is the SETTING being 1. Reading it from the limit made a
  // two-option question in a chapter allowing 2 attempts Save only, with its
  // check's verdict withheld (issue #92): the option count had lowered the
  // limit to 1.
  const isObjectiveAutoGraded =
    questionType === 'MULTIPLE_CHOICE' ||
    questionType === 'NUMERICAL_WITH_ERROR' ||
    questionType === 'EXACT_ANSWER' ||
    questionType === 'SELECT_ALL';
  const isSingleAttempt = isObjectiveAutoGraded && isSingleAttemptMode === true;
  const locked = isSingleAttempt
    ? false
    : maxAttempts !== undefined
      ? attemptsUsed >= maxAttempts
      : false;
  const participationOnly = isParticipationOnly(question, settings);
  // This id is the question's identity for the entire interactive UI: it is the
  // radio-group `name`, the key for the per-question answer / random-answer /
  // submission maps (see QuestionCard), and the target a submission updates. It
  // MUST be unique within a section across every nesting level.
  //
  // When the backend supplies no stable id/uid we synthesize one from POSITION.
  // A nested part's position is only meaningful RELATIVE TO ITS PARENT, so its
  // synthetic id is derived from the parent's id, NOT from the section + the local
  // part index. Deriving it from the local part index alone made "Part a" of every
  // multi-part question collide on the same id (e.g. `<section>-question-1`), which
  // cross-linked their answers — selecting question 1's Part a flipped question 3's
  // Part a, and a submission updated every colliding part at once. Using the parent
  // id keeps each parent's parts in their own namespace; the distinct `-part-`
  // segment also prevents a part from colliding with a top-level question that
  // happens to share the same index. parentQuestionId is itself unique (a real id
  // or the section-scoped fallback below), so this stays unique at any depth.
  const questionId =
    normalizeIdentifier(question.id) ??
    normalizeIdentifier(question.uid) ??
    (parentQuestionId !== undefined
      ? `${parentQuestionId}-part-${questionIndex + 1}`
      : `${questionSectionId}-question-${questionIndex + 1}`);
  const questionUid =
    normalizeIdentifier(question.uid) ??
    normalizeIdentifier(question.id) ??
    questionId;
  const gradingRubric =
    questionType === 'AI_UPLOAD'
      ? normalizeGradingRubric(question, questionId)
      : undefined;
  const isPastDue = timingState?.isPastDue ?? false;
  const isInstructorStatsMode = isInstructorRequest && sectionHasAggregateStats;
  const isReviewMode = isPastDue || isInstructorStatsMode;
  const solutionsVisible =
    timingState?.solutionsVisible ??
    shouldShowSolution(question, component, settings, now);
  const pointsPossible =
    questionType === 'MULTI_PART'
      ? getPointsPossible(question, 0)
      : questionType === 'AI_UPLOAD'
        ? normalizeNumber(question.value) ?? gradingRubric?.totalScore ?? 1
      : getPointsPossible(question);
  const feedback = isInstructorRequest
    ? undefined
    : createFeedback(
        question,
        questionType,
        questionStats,
        pointsPossible,
        participationOnly,
        settings,
        isSingleAttempt && !isPastDue,
      );
  const hint = createDisclosure(
    'Hint',
    getContentSection(question.hint),
    shouldShowHint(
      questionType,
      question,
      attemptsUsed,
      settings,
      isPastDue,
      solutionsVisible,
    ),
    replacements,
  );
  const solution = createDisclosure(
    'Solution',
    getContentSection(question.detailed_answer ?? question.detailedAnswer),
    solutionsVisible,
    replacements,
  );
  const scoreEarned =
    getScore(questionStats) ?? feedback?.earnedPoints ?? undefined;
  // A student who has earned this question's correctness credit keeps their
  // graded random answer untouched (mirrors the "correct" feedback threshold at
  // createFeedback). Hybrid participation-only awards stay below pointsPossible,
  // so they do NOT count as credit and are still realigned to what's displayed.
  const studentHasCredit = pointsPossible > 0 && (scoreEarned ?? 0) >= pointsPossible;

  const baseQuestion = {
    aiHintThreshold,
    // See BaseQuestionData.answerFormula: instructors only.
    answerFormula: isInstructorRequest
      ? normalizeString(
          question.variable_based_answer ?? question.variableBasedAnswer,
        ) ?? undefined
      : undefined,
    allowAIHints: resolveAllowAIHints(settings),
    allowAIPracticeProblems: resolveAllowAiPracticeProblems(settings),
    allowAIAssistant: resolveAllowAiAssistant(settings),
    attemptsLabel: getAttemptsLabel(attemptsUsed, maxAttempts),
    attemptsUsed,
    canCheck: isAssigned && questionType !== 'MULTI_PART' && !locked && !isReviewMode,
    canSave: isAssigned && questionType !== 'MULTI_PART' && !locked && !isReviewMode,
    feedback,
    hasAggregateStats: sectionHasAggregateStats,
    hasResponse: hasAnsweredQuestion(questionStats),
    hintSetting,
    hint,
    id: questionId,
    isAssigned,
    imagePaths,
    isInstructorView: isInstructorRequest,
    isInstructorStatsMode,
    isLocked: locked || isPastDue,
    isParticipationOnly: participationOnly,
    isPastDue,
    isReviewMode,
    isSingleAttemptMode: isObjectiveAutoGraded ? isSingleAttempt : undefined,
    label:
      partLabel ??
      (kind === 'preLecture'
        ? `Pre-Lecture Question ${questionIndex + 1}`
        : `Question ${questionIndex + 1}`),
    maxAttempts,
    partLabel,
    parentQuestionId,
    pointsPossible,
    promptUnits,
    questionSectionId,
    scoreEarned,
    sectionId,
    solution,
    solutionsAvailableAtUtcMilli: timingState?.solutionsVisibleAtUtcMilli,
    solutionsVisible,
    uid: questionUid,
    usesRandomVariables,
    usesRepresentativeVariableValues,
  } satisfies Omit<BaseQuestionData, 'kind'>;

  if (questionType === 'MULTIPLE_CHOICE') {
    return {
      answerIndexStats: getAnswerIndexStats(question, questionStats),
      correctAnswerIndex: getCorrectAnswerIndex(question),
      ...baseQuestion,
      currentRandomExactAnswer:
        getResolvedRandomExactAnswer(answer) ??
        resolvedRandomVariables?.correctAnswer.exact,
      currentRandomNumericalAnswer:
        getResolvedRandomNumericalAnswer(answer) ??
        resolvedRandomVariables?.correctAnswer.numerical,
      currentRandomNumericalAnswerExtraExponent:
        getResolvedRandomNumericalExponent(answer) ??
        resolvedRandomVariables?.correctAnswer.extraExponent,
      kind: 'multipleChoice',
      options: normalizeMultipleChoiceOptions(
        question,
        replacements,
        resolvedRandomVariables?.generatedOptions,
      ),
      selectedIndex: getSelectedIndex(answer),
      units:
        normalizeString(question.answer_units ?? question.answerUnits) ?? undefined,
    };
  }

  if (questionType === 'SELECT_ALL') {
    return {
      answerIndexStats: getAnswerIndexStats(question, questionStats),
      ...baseQuestion,
      correctAnswerIndices: getCorrectAnswerIndices(question),
      kind: 'selectAll',
      // Options normalize exactly as multiple choice does — text and
      // content_options both — so the two types cannot drift in how an option
      // is read.
      options: normalizeMultipleChoiceOptions(
        question,
        replacements,
        resolvedRandomVariables?.generatedOptions,
      ),
      selectedIndices: getSelectedIndices(answer),
      units:
        normalizeString(question.answer_units ?? question.answerUnits) ?? undefined,
    };
  }

  if (questionType === 'NUMERICAL_WITH_ERROR') {
    const {
      currentRandomNumericalAnswer,
      currentRandomNumericalAnswerExtraExponent,
      currentRandomVariables,
    } = resolveNumericalRandomAnswer({
      answer,
      resolvedRandomVariables,
      questionVariables,
      storedVariablesWereInjected,
      studentHasCredit,
    });

    return {
      ...baseQuestion,
      correctAnswer: getCorrectNumericalAnswer(
        question,
        answer,
        resolvedRandomVariables,
      ),
      correctAnswerExtraExponent: getCorrectNumericalExponent(
        question,
        answer,
        resolvedRandomVariables,
      ),
      currentRandomNumericalAnswer,
      currentRandomNumericalAnswerExtraExponent,
      currentRandomVariables,
      errorTolerance:
        normalizeNumber(question.numerical_error ?? question.numericalError) ??
        undefined,
      inputValue: getNumericalInputValue(answer),
      kind: 'numerical',
      units:
        normalizeString(question.answer_units ?? question.answerUnits) ?? undefined,
    };
  }

  if (questionType === 'EXACT_ANSWER') {
    return {
      ...baseQuestion,
      correctAnswer: getCorrectExactAnswer(question, answer, resolvedRandomVariables),
      currentRandomExactAnswer:
        getResolvedRandomExactAnswer(answer) ??
        resolvedRandomVariables?.correctAnswer.exact,
      inputValue: getExactInputValue(answer),
      kind: 'exactAnswer',
      units:
        normalizeString(question.answer_units ?? question.answerUnits) ?? undefined,
    };
  }

  if (questionType === 'AI_UPLOAD') {
    const submittedFileUrl =
      getFileUploadUrl(answer) ??
      normalizeGradingDetails(questionStats)?.uploadFileUrl;

    return {
      ...baseQuestion,
      canSave: false,
      gradingDetails: normalizeGradingDetails(questionStats),
      gradingRubric: gradingRubric ?? normalizeGradingRubric(question, questionId),
      kind: 'aiUpload',
      submittedFileName: getFileNameFromUrl(submittedFileUrl),
      submittedFileUrl,
    };
  }

  const nestedQuestions = toQuestionArray(
    question.question_set?.questions ?? question.questionSet?.questions,
  )
    .map((partQuestion, partIndex) =>
      normalizeQuestionInternal(
        withInheritedRandomVariables(
          partQuestion,
          usesRandomVariables ? questionVariables : [],
        ),
        component,
        kind,
        sectionId,
        questionSectionId,
        partIndex,
        getNestedQuestionStat(questionStats, partQuestion, partIndex),
        sectionHasAggregateStats,
        now,
        isAssigned,
        isInstructorRequest,
        timingState,
        kuduUserId,
        getPartLabel(partIndex),
        questionId,
      ),
    )
    .filter((part): part is NormalizedQuestionData => part !== null);
  const normalizedPointsPossible =
    pointsPossible > 0
      ? pointsPossible
      : nestedQuestions.reduce(
          (total, partQuestion) => total + partQuestion.pointsPossible,
          0,
        );

  return {
    ...baseQuestion,
    canCheck: false,
    canSave: false,
    feedback:
      baseQuestion.feedback &&
      normalizedPointsPossible !== baseQuestion.pointsPossible
        ? {
            ...baseQuestion.feedback,
            possiblePoints: normalizedPointsPossible,
          }
        : baseQuestion.feedback,
    kind: 'multiPart',
    parts: nestedQuestions,
    pointsPossible: normalizedPointsPossible,
  };
}

function getSectionScore(
  sectionStats: RawSectionStats | null,
  questions: NormalizedQuestionData[],
  kind: QuestionSectionKind,
) {
  const explicitScore =
    kind === 'preLecture'
      ? normalizeNumber(
          sectionStats?.total_pre_lecture_score ?? sectionStats?.totalPreLectureScore,
        )
      : normalizeNumber(sectionStats?.total_score ?? sectionStats?.totalScore);

  if (explicitScore !== undefined) {
    return explicitScore;
  }

  const earnedScores = questions
    .map((question) => question.feedback?.earnedPoints)
    .filter((score): score is number => score !== undefined);

  return earnedScores.length > 0
    ? earnedScores.reduce((total, score) => total + score, 0)
    : undefined;
}

export function normalizeQuestionSections(
  component: RawComponent,
  options?: {
    coursePrelectureQuestionSettings?: RawQuestionSettings;
    courseQuestionSettings?: RawQuestionSettings;
    isInstructorRequest?: boolean;
    kuduUserId?: string;
    now?: number;
    timingState?: ComponentTimingState;
  },
): NormalizedQuestionSectionData[] {
  const now = options?.now ?? Date.now();
  const isInstructorRequest = options?.isInstructorRequest ?? false;
  const kuduUserId = options?.kuduUserId;
  const timingState = options?.timingState;
  const componentForNormalization: RawComponent = {
    ...component,
    course_prelecture_question_settings:
      options?.coursePrelectureQuestionSettings ??
      component.course_prelecture_question_settings,
    course_question_settings:
      options?.courseQuestionSettings ?? component.course_question_settings,
    coursePrelectureQuestionSettings:
      options?.coursePrelectureQuestionSettings ??
      component.coursePrelectureQuestionSettings,
    courseQuestionSettings:
      options?.courseQuestionSettings ?? component.courseQuestionSettings,
  };
  const rawSections = Array.isArray(componentForNormalization.sections)
    ? componentForNormalization.sections
    : [];
  const sectionKinds: QuestionSectionKind[] = ['preLecture', 'homework'];

  return sectionKinds.flatMap((kind) => {
    const totalQuestionSections = rawSections.reduce((total, section) => {
      const questionSection = getQuestionSection(section, kind);

      return questionSection && toQuestionArray(questionSection.questions).length > 0
        ? total + 1
        : total;
    }, 0);

    let emittedSectionIndex = 0;

    const sectionsForKind = rawSections
      .map((section, sectionIndex) => {
        const questionSection = getQuestionSection(section, kind);

        if (!questionSection) {
          return null;
        }

        const rawQuestions = toQuestionArray(questionSection.questions);

        if (rawQuestions.length === 0) {
          return null;
        }

        const sectionId = getSectionIdentifier(section, sectionIndex);
        const questionSectionId = getQuestionSectionIdentifier(
          section,
          questionSection,
          sectionIndex,
        );
        const sectionStats = getSectionStats(
          componentForNormalization,
          sectionId,
          sectionIndex,
        );
        const rawQuestionStats = getRawQuestionStats(sectionStats, kind);
        const explicitSectionTotalScore =
          kind === 'preLecture'
            ? normalizeNumber(
                sectionStats?.total_pre_lecture_score ??
                  sectionStats?.totalPreLectureScore,
              )
            : normalizeNumber(sectionStats?.total_score ?? sectionStats?.totalScore);
        const sectionTotalScore = explicitSectionTotalScore ?? 0;
        const sectionHasAggregateStats =
          rawQuestionStats.length > 0 || explicitSectionTotalScore !== undefined;
        const settings = getQuestionSettings(componentForNormalization, kind);
        const assignedQuestionIndexes = getAssignedQuestionIndexes(
          rawQuestions,
          settings,
        );
        const scopedTimingState = timingState
          ? kind === 'preLecture'
            ? {
                ...timingState,
                isPastDue: timingState.isPreLecturePastDue,
                solutionsVisible: timingState.preLectureSolutionsVisible,
                solutionsVisibleAtUtcMilli:
                  timingState.preLectureSolutionsVisibleAtUtcMilli,
              }
            : timingState
          : undefined;
        const questions = rawQuestions
          .map((question, questionIndex) => {
            const isRemoved = normalizeBoolean(question.removed) === true;
            const normalized = normalizeQuestionInternal(
              question,
              componentForNormalization,
              kind,
              sectionId,
              questionSectionId,
              questionIndex,
              getQuestionStat(sectionStats, question, questionIndex, kind),
              sectionHasAggregateStats,
              now,
              assignedQuestionIndexes.has(questionIndex) && !isRemoved,
              isInstructorRequest,
              scopedTimingState,
              kuduUserId,
            );
            return normalized && isRemoved ? { ...normalized, isRemoved } : normalized;
          })
          .filter((question): question is NormalizedQuestionData => question !== null);

        if (questions.length === 0) {
          return null;
        }

        const assignedQuestions = questions.filter((question) => question.isAssigned);
        const headingIndex = emittedSectionIndex;
        emittedSectionIndex += 1;

        return {
          // The timing state's end time is already the viewer's own: a
          // student's time extension replaces the chapter's due date before
          // the chapter is normalized.
          closesAtUtcMilli:
            kind === 'preLecture'
              ? timingState?.preLectureEndTimeUtcMilli
              : timingState?.endTimeUtcMilli,
          earnedPoints: getSectionScore(sectionStats, assignedQuestions, kind),
          hasAggregateStats: sectionHasAggregateStats,
          heading: getQuestionSectionHeading(
            kind,
            headingIndex,
            totalQuestionSections,
          ),
          id: sectionId,
          isInstructorView: isInstructorRequest,
          isPastDue: scopedTimingState?.isPastDue ?? false,
          kind,
          possiblePoints: assignedQuestions.reduce(
            (total, question) => total + question.pointsPossible,
            0,
          ),
          questionCount: questions.length,
          questionSectionId,
          questions,
          rawQuestionStats,
          sectionNumAttempts: getSectionNumAttempts(sectionStats, kind),
          sectionStatsId: getSectionStatsId(sectionStats, sectionId),
          sectionTotalScore,
          solutionsVisible: scopedTimingState?.solutionsVisible ?? false,
        } satisfies NormalizedQuestionSectionData;
      });

    return sectionsForKind.filter(
      (
        section,
      ): section is NonNullable<(typeof sectionsForKind)[number]> => section !== null,
    );
  });
}
